import React from 'react';
import { FaDownload } from 'react-icons/fa6';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';

export interface Column<Row> {
  key: keyof Row | string;
  label: string;
  render?: (row: Row) => React.ReactNode;
  rightAlign?: boolean;
}

interface Props<Row> {
  title: string;
  subtitle?: string;
  columns: Column<Row>[];
  rows: Row[];
  loading: boolean;
  error: string | null;
  onDownloadCsv: () => void | Promise<void>;
  rowKey: (row: Row, idx: number) => React.Key;
  headerActions?: React.ReactNode;
  /** Optional filter bar rendered above the table. Reports that only take
   *  their filters from the report card on /new-report leave this out. */
  filters?: React.ReactNode;
  parentPage?: string;
  parentPath?: string;
}

export function PredefinedResultsLayout<Row>(props: Props<Row>) {
  const {
    title,
    subtitle,
    columns,
    rows,
    loading,
    error,
    onDownloadCsv,
    rowKey,
    headerActions,
    filters,
    // Every predefined report is launched from the Predefined Reports tab, so
    // that is where the crumb goes back to.
    parentPage = 'Reports',
    parentPath = '/new-report?view=predefined',
  } = props;

  return (
    <>
      <Breadcrumb pageName={title} parentPage={parentPage} parentPath={parentPath} />
      <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5'>
        <div className='flex items-start justify-between mb-4'>
          <div>
            {subtitle && <p className='text-sm text-body'>{subtitle}</p>}
            {!loading && !error && (
              <p className='text-xs text-body mt-1'>{rows.length} row{rows.length === 1 ? '' : 's'}</p>
            )}
          </div>
          <div className='flex items-center gap-3'>
            {headerActions}
            <button
              type='button'
              onClick={onDownloadCsv}
              disabled={loading || rows.length === 0}
              className='inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
            >
              <FaDownload /> Download CSV
            </button>
          </div>
        </div>

        {filters && (
          <div className='mb-4 border-y border-stroke py-4'>{filters}</div>
        )}

        {loading && <p className='py-12 text-center text-body'>Loading…</p>}
        {error && <p className='py-12 text-center text-danger'>{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p className='py-12 text-center text-body'>No results.</p>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full table-auto text-sm'>
              <thead>
                <tr className='bg-gray-2 text-left'>
                  {columns.map((c) => (
                    <th
                      key={String(c.key)}
                      className={`px-3 py-2 font-medium ${c.rightAlign ? 'text-right' : ''}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={rowKey(row, idx)} className='border-t border-stroke'>
                    {columns.map((c) => (
                      <td
                        key={String(c.key)}
                        className={`px-3 py-2 ${c.rightAlign ? 'text-right' : ''}`}
                      >
                        {c.render ? c.render(row) : ((row as any)[c.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
